<script setup lang="ts">
/*
  A new dataspace: its label, the project it runs, and which version of it.

  The version is a PICK, not free text: the server runs only a version with a build on it and
  refuses any other (`version_not_built`), so the picker offers what can run — and starts on
  the project's latest release, or on `wip` before there is one.

  WHERE IT GOES is a pick too: personal, or a department the caller may create dataspaces in
  (`department:dataspaces:create`). It starts on the chosen project's department when the caller
  may create there — a dataspace usually belongs with the project it runs — and on personal
  otherwise, until the caller picks for themselves; from then on the project no longer decides.
  Personal is sent as an explicit `null`: left out, the server would choose the project's
  department itself, and the dialog must say what it showed.

  What the server's license allows shapes the choice, as it does the create-project dialog: with
  no personal spaces "Personal" is not offered and the dialog starts on a department, and with
  nowhere to put a dataspace it says so. `spaceError` words the refusals by code for a license that
  changed after the session read it.

  AN ALIAS is optional: what its URL names it by instead of its id (`AliasField`). One the server
  would refuse keeps the button off; one another dataspace has is said under the field.
*/
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { HabBanner, HabButton, HabInput, HabModal, HabSelect } from "@livereader/habitus-vue";
import type { HabSelectOption } from "@livereader/habitus-vue";
import {
	API_ERROR_CODES,
	ApiCallError,
	createDataspace,
	listProjects,
	type DataspaceResponse,
	type ProjectResponse,
} from "@/api";
import SpaceLocationSelect from "@/shared/components/SpaceLocationSelect.vue";
import { useAuth } from "@/shared/composables/useAuth";
import { useSystemAccess } from "@/shared/composables/useSystemAccess";
import { useMyDepartments } from "@/shared/composables/useMyDepartments";
import { aliasProblem, normalizeAlias } from "@/shared/utils/alias";
import { errorMessage } from "@/shared/utils/errors";
import { spaceError } from "@/shared/utils/spaceErrors";
import {
	CREATE_DATASPACES,
	PERSONAL,
	departmentIdOf,
	personalSpacesAllowed,
	startingLocation,
} from "@/shared/utils/spaceLocation";
import AliasField from "@operator/components/_shared/AliasField.vue";
import { useProjectVersionOptions } from "@operator/composables/dataspace/useProjectVersionOptions";
import { isVersionNotBuilt, WIP } from "@operator/utils/dataspace/projectVersions";

const props = defineProps<{
	show: boolean;
}>();

const emit = defineEmits<{
	close: [];
	created: [dataspace: DataspaceResponse];
}>();

const { t } = useI18n();

const label = ref("");
const description = ref("");
const alias = ref("");
/** The alias the server said another dataspace has, normalized. */
const takenAlias = ref<string | null>(null);
const projectId = ref("");
const projectVersion = ref(WIP);
const loading = ref(false);
const loadingProjects = ref(false);
const error = ref("");
const projectOptions = ref<HabSelectOption[]>([]);
/** The projects the dialog offers, by id: each says which department it is in. */
const projects = ref<ProjectResponse[]>([]);

const mine = useMyDepartments();
const { licenseFeatures } = useAuth();
const access = useSystemAccess();
/** Naming a dataspace is a right of the whole server, as its URLs are one namespace. */
const canAlias = computed(() => access.can("system:alias_dataspaces"));
const allowed = computed(() => mine.granting(CREATE_DATASPACES));
const personal = computed(() => personalSpacesAllowed(licenseFeatures.value));
const location = ref(PERSONAL);
const askingDepartments = ref(false);
/** Nowhere to put a dataspace: no personal spaces, and no department to create in. Said once asked. */
const noPlace = computed(
	() => !askingDepartments.value && !personal.value && allowed.value.length === 0,
);
/** The caller picked a location themselves: the project's department no longer decides it. */
const locationPicked = ref(false);

// The project's department, where the caller may create — else personal. Re-decided as what the
// caller may do arrives and as the project changes, until they choose.
watch([projectId, allowed, personal], () => {
	if (locationPicked.value) return;
	const project = projects.value.find((p) => p.id === projectId.value);
	location.value = startingLocation(allowed.value, project?.department?.id, personal.value);
});

function onLocationPicked(next: string) {
	locationPicked.value = true;
	location.value = next;
}

const versions = useProjectVersionOptions(() => projectId.value || null);

// Each project starts on its own latest release, once its versions are known.
watch(
	() => versions.known.value,
	(known) => {
		if (known) projectVersion.value = versions.preferred.value;
	},
);

const canSubmit = computed(
	() =>
		label.value.trim().length > 0 &&
		!!projectId.value &&
		!!projectVersion.value &&
		!noPlace.value &&
		!(canAlias.value && aliasProblem(normalizeAlias(alias.value))) &&
		!loading.value,
);

watch(
	() => props.show,
	async (open) => {
		if (!open) return;
		label.value = "";
		description.value = "";
		alias.value = "";
		takenAlias.value = null;
		projectId.value = "";
		projectVersion.value = WIP;
		location.value = startingLocation([], null, personal.value);
		locationPicked.value = false;
		error.value = "";
		loadingProjects.value = true;
		askingDepartments.value = true;
		// What the caller may do in their departments, asked beside the project list.
		void mine.ready().finally(() => {
			askingDepartments.value = false;
			// Decided again now that it is known where a dataspace may go.
			if (!locationPicked.value) {
				const project = projects.value.find((p) => p.id === projectId.value);
				location.value = startingLocation(
					allowed.value,
					project?.department?.id,
					personal.value,
				);
			}
		});
		try {
			projects.value = await listProjects();
			projectOptions.value = projects.value.map((p) => ({
				value: p.id,
				label: p.name ?? p.id.slice(0, 8),
			}));
		} catch (e) {
			error.value = errorMessage(e);
		} finally {
			loadingProjects.value = false;
		}
	},
);

async function onCreate() {
	if (!canSubmit.value) return;
	loading.value = true;
	error.value = "";
	try {
		const dataspace = await createDataspace({
			label: label.value.trim(),
			description: description.value.trim() || undefined,
			project_id: projectId.value,
			project_version: projectVersion.value,
			department_id: departmentIdOf(location.value),
			alias: canAlias.value ? alias.value.trim() || undefined : undefined,
		});
		emit("created", dataspace);
		emit("close");
	} catch (e) {
		if (e instanceof ApiCallError && e.code === API_ERROR_CODES.aliasTaken) {
			takenAlias.value = normalizeAlias(alias.value);
		}
		error.value = isVersionNotBuilt(e)
			? t("operator.dataspace.versionNotBuilt", { version: projectVersion.value })
			: spaceError(e);
	} finally {
		loading.value = false;
	}
}
</script>

<template>
	<HabModal
		:open="show"
		:title="$t('operator.dataspace.create')"
		:close-label="$t('common.actions.close')"
		@update:open="(open: boolean) => !open && emit('close')"
	>
		<div class="flex flex-col gap-4">
			<HabBanner
				v-if="error"
				tone="error"
				:title="error"
			/>
			<HabBanner
				v-if="noPlace"
				tone="warning"
				:title="$t('spaceRestriction.picker.noPlace')"
			/>

			<HabInput
				v-model="label"
				:label="$t('common.fields.label')"
				autofocus
				@keydown.enter="onCreate"
			/>

			<HabInput
				v-model="description"
				:label="$t('common.fields.description')"
				multiline
				auto-grow
				:rows="2"
			/>

			<AliasField
				v-if="canAlias"
				v-model="alias"
				prefix="/operator/"
				:taken="takenAlias"
			/>

			<!--
				HabSelect has no busy state, so the list is disabled until it has arrived
				rather than opening empty.
			-->
			<HabSelect
				v-model="projectId"
				:options="projectOptions"
				:label="$t('operator.dataspace.project')"
				select-single
				:placeholder="loadingProjects ? t('common.status.loading') : undefined"
				:state="loadingProjects ? 'disabled' : 'default'"
			/>

			<HabSelect
				v-model="projectVersion"
				:options="versions.options.value"
				:label="$t('operator.dataspace.version')"
				:help="$t('operator.dataspace.versionHelp')"
				:placeholder="versions.loading.value ? t('common.status.loading') : undefined"
				:state="!projectId || versions.loading.value ? 'disabled' : 'default'"
			/>

			<SpaceLocationSelect
				:model-value="location"
				:allowed="allowed"
				:loading="askingDepartments"
				:personal="personal"
				@update:model-value="onLocationPicked"
			/>
		</div>

		<template #actions>
			<HabButton
				variant="text"
				tone="neutral"
				@click="emit('close')"
			>
				{{ $t("common.actions.cancel") }}
			</HabButton>
			<HabButton
				:loading="loading"
				:disabled="!canSubmit"
				@click="onCreate"
			>
				{{ $t("common.actions.create") }}
			</HabButton>
		</template>
	</HabModal>
</template>
